import * as React from 'react';
import { useState, useEffect } from 'react';
import EventBus from '../EventBus';
import EnergipresentationAccordion from './EnergipresentationAccordion';
import iTextReplacements from './iTextReplacements';
import { isGraphData } from './ResponseModels/iGraphData';
import DisplayHelper from './DisplayHelper';
import * as Highcharts from 'highcharts';
import { HighchartsReact } from 'highcharts-react-official';
import 'highcharts/modules/accessibility';
import 'highcharts/modules/pattern-fill';
import 'highcharts/modules/sankey';

export interface iEnergipresentationGraphViewerModel {
    Type: string; // 'VIEWER_DATA'
    Id: string;
    Payload: any;
    ViewType: 'sankey' | 'pie' | 'area' | 'spline' | 'column' | 'scale';
    DocumentTitle: string;
    Title: string;
    TextReplacements: iTextReplacements;
    ColumnHeaders: string;
    InvertTableAxis: boolean;
    AccordionTitle: string;
    AccordionText: string;
    AccordionTable: string;
    AccordionStyle: string;
};

const GraphViewer = () => {

    const [state, setState] = useState<iEnergipresentationGraphViewerModel>({} as iEnergipresentationGraphViewerModel);

    useEffect(() => {
        const handler = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) {
                return;
            }

            const data = { ...event.data } as iEnergipresentationGraphViewerModel;

            if (data.Type === 'VIEWER_DATA' && data.Payload) {
                document.title += ` - ${data.Title ?? DisplayHelper.LocaleText('Graph', 'Graf')}`;

                const container = document.getElementsByClassName('graph-viewer-react-container')[0];
                if (container) {
                    container.id = data.Id;
                }

                if (isGraphData(data.Payload) && DisplayHelper.HasPrognoseData(data.Payload)) {
                    DisplayHelper.DisplayPrognoseArea(data.Payload);
                }

                setState(data);
                EventBus.dispatch("GraphDataLoaded", event.data.Id);
            }
        };

        window.addEventListener('message', handler);

        // Tell opener we're ready for data
        window.opener?.postMessage("READY_FOR_DATA", window.location.origin);

        return () => {
            window.removeEventListener('message', handler);
        };
    }, []);


    const hasValues = (list: Array<any>): boolean => {
        return list !== undefined && list !== null && list.length > 0;
    };

    const hasAllValues = (data: any): boolean => {
        if (!data) { return false; }
        if (typeof data === 'number') { return true; }
        if (typeof data === 'string') { return data.length > 0; }
        if (Array.isArray(data)) {
            if (!data.length) { return false; }
            let i = 0;
            for (let item in data) {
                if (!hasAllValues(item,)) { return false; };
            }
            return true;
        }

        if (!hasValues(Object.keys(data))) { return false; }
        for (let key of Object.keys(data)) {
            if (!hasAllValues(data[key])) { return false; }
        }
        return true;
    };

    const getContainerClassName = () => {
        return (state.ViewType === 'pie' ? 'circle'
            : state.ViewType !== 'sankey' ? 'graph'
                : state.ViewType) + "-container";
    };

    return (
        <React.Fragment>
            <h3>{state.Title}</h3>
            {state?.ViewType === 'scale' ? //first 2 divs are just to get the CSS to work as is...  perhaps remove and alter CSS structure instead?
                <div className='energipresentation-scales-block'>
                    <div className='energipresentation-scales-block-react-container'>
                        <div className="scale-wrapper">
                            <div className="energipresentation-scale-container">
                                <div className="scroll-container">
                                    <div className="image-container">
                                        <img src="/Static/img/EnergipresentationScale.png" />
                                        <div className="left-side">
                                            <div className="title"><div>{state.Payload.titleLeftSide}</div></div>
                                            {hasValues(state.Payload.sourceAValues) && (
                                                <React.Fragment>
                                                    {state.Payload.sourceAValues.map((item, itemIndex) => {
                                                        return (
                                                            <div key={"sourceA-Item-" + itemIndex} className={"box-text box-" + itemIndex}><div>{item.key}&nbsp;<span className="span-value">{item.value}</span></div></div>
                                                        )
                                                    })}
                                                </React.Fragment>
                                            )}
                                            <div className="box-text left-total"><p>{state.Payload.sourceATotal}</p></div>
                                        </div>
                                        <div className="right-side">
                                            <div className="title"><div>{state.Payload.titleRightSide}</div></div>
                                            <div className="box-text difference-box"><div><span className="span-value">{state.Payload.statisticDifference}*</span></div></div>
                                            {hasValues(state.Payload.sourceCValues) && (
                                                <React.Fragment>
                                                    {state.Payload.sourceCValues.map((item, itemIndex) => {
                                                        return (
                                                            <div key={"sourceC-Item-" + itemIndex} className={"box-text box-" + (itemIndex)}><div>{item.key}&nbsp;<span className="span-value">{item.value}</span></div></div>
                                                        )
                                                    })}
                                                </React.Fragment>
                                            )}
                                            {hasValues(state.Payload.sourceBValues) && (
                                                <React.Fragment>
                                                    {state.Payload.sourceBValues.map((item, itemIndex) => {
                                                        return (
                                                            <div key={"sourceB-Item-" + itemIndex} className={"box-text box-" + (itemIndex + state.Payload.sourceCValues.length)}><div>{item.key}&nbsp;<span className="span-value">{item.value}</span></div></div>
                                                        )
                                                    })}
                                                </React.Fragment>
                                            )}
                                            <div className="box-text right-total"><p>{state.Payload.sourceRightSideTotal}</p></div>
                                        </div>
                                        <div className="current-year-box"><p>{state.Payload.year}</p></div>
                                        <div className="description-difference-box"><p>{state.Payload.descriptionDifference}</p></div>
                                    </div>
                                    <div className="right-side-totals">
                                        <div className="top-total">
                                            <div className="text">
                                                {state.Payload.resultTextSourceC}
                                                <div className="value">{state.Payload.sourceCTotal} TWh</div>
                                            </div>
                                        </div>
                                        <div className="bottom-total">
                                            <div className="text">
                                                {state.Payload.resultTextSourceB}
                                                <div className="value">{state.Payload.sourceBTotal} TWh</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                :
                <div className={getContainerClassName()}>
                    <div className="scroll-container">
                        {state.Payload && (
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={state.Payload}
                            />
                        )}
                    </div>
                </div>
            }
            {(state.AccordionText && state.AccordionText != "") && (
                <EnergipresentationAccordion
                    key={"accordion_" + 'graph-view'}
                    title={state.AccordionTitle}
                    text={state.AccordionText}
                    table={state.AccordionTable}
                    modelStyle={state.AccordionStyle}
                />
            )}
        </React.Fragment >
    );
};
export default GraphViewer;